import React, {useCallback, useMemo} from 'react';
import {Box, Flex, HStack, Text} from '@tempo/core';
import {CheckIcon} from '@tempo/icons';
import * as dateFns from 'date-fns';
import type {DateRangePickerProps, RangeSelectorType} from '../types';
import {PeriodType} from '../types';
import {useLocalizedFormat} from '@deezer/next/contexts/localization';
import {toZonedTime} from 'date-fns-tz';
import type {TFunction} from 'next-i18next';
import {useTranslation} from 'next-i18next';
import {getCurrentPitchingWeek, getNextPitchingWeek} from './utils';

export const getPeriodText = (
	t: TFunction,
	period?: PeriodType,
): string | undefined => {
	switch (period) {
		case PeriodType.LAST_7_DAYS:
			return t('timefilter_text_lastXdays_d4c-web', {
				sprintf: [7],
				count: 7,
			});

		case PeriodType.CURRENT_PITCHING_WEEK:
			return t('timefilter_text_currentweek_d4c-web');
		case PeriodType.NEXT_PITCHING_WEEK:
			return t('timefilter_text_nexweek_d4c-web');
		case PeriodType.LAST_4_WEEKS:
			return t('timefilter_text_lastXweeks_d4c-web', {
				sprintf: [4],
				count: 4,
			});

		case PeriodType.LAST_8_WEEKS:
			return t('timefilter_text_lastXweeks_d4c-web', {
				sprintf: [8],
				count: 8,
			});

		case PeriodType.LAST_12_WEEKS:
			return t('timefilter_text_lastXweeks_d4c-web', {
				sprintf: [12],
				count: 12,
			});
		case PeriodType.SINCE_START_YEAR:
			return t('timefilter_text_sincestartofyear_d4c-web');
		default:
			return undefined;
	}
};

export const datePickerDataTestId = (
	period?: PeriodType,
): string | undefined => {
	switch (period) {
		case PeriodType.LAST_7_DAYS:
			return 'datepicker-period-selector-last-7-days';
		case PeriodType.CURRENT_PITCHING_WEEK:
			return 'datepicker-period-selector-current-pitching-week';
		case PeriodType.NEXT_PITCHING_WEEK:
			return 'datepicker-period-selector-next-pitching-week';
		case PeriodType.LAST_4_WEEKS:
			return 'datepicker-period-selector-last-4-weeks';
		case PeriodType.LAST_8_WEEKS:
			return 'datepicker-period-selector-last-8-weeks';
		case PeriodType.LAST_12_WEEKS:
			return 'datepicker-period-selector-last-12-weeks';
		case PeriodType.SINCE_START_YEAR:
			return 'datepicker-period-selector-since-start-year';
		default:
			return undefined;
	}
};

export const getPeriodDates = (
	period: PeriodType,
): {startDate: Date; endDate: Date} => {
	let now = new Date();
	let fromDate = dateFns.subWeeks(now, 1);
	switch (period) {
		case PeriodType.LAST_7_DAYS:
			fromDate = dateFns.subWeeks(now, 1);
			break;
		case PeriodType.CURRENT_PITCHING_WEEK:
			now = getCurrentPitchingWeek().now;
			fromDate = getCurrentPitchingWeek().fromDate;
			break;
		case PeriodType.NEXT_PITCHING_WEEK:
			now = getNextPitchingWeek().now;
			fromDate = getNextPitchingWeek().fromDate;
			break;
		case PeriodType.LAST_4_WEEKS:
			fromDate = dateFns.subWeeks(now, 4);
			break;
		case PeriodType.LAST_8_WEEKS:
			fromDate = dateFns.subWeeks(now, 8);
			break;
		case PeriodType.LAST_12_WEEKS:
			fromDate = dateFns.subWeeks(now, 12);
			break;
		case PeriodType.SINCE_START_YEAR:
			fromDate = dateFns.startOfYear(now);
			break;
	}

	return {
		startDate: fromDate,
		endDate: now,
	};
};

type PeriodProps = {
	period: PeriodType;
	isActive?: boolean;
	onClick: DateRangePickerProps<RangeSelectorType.PERIOD>['onChange'];
	isUTC?: boolean;
};

const Period = ({period, isActive, onClick, isUTC}: PeriodProps) => {
	const {formatDate} = useLocalizedFormat();
	const {t} = useTranslation();
	const handleClick = useCallback(() => {
		const timeRange = getPeriodDates(period);
		onClick({...timeRange, period});
	}, [onClick, period]);

	const periodTimeLabel = useMemo(() => {
		let now = new Date();
		let fromDate = dateFns.subWeeks(now, 1);
		switch (period) {
			case PeriodType.LAST_7_DAYS:
				fromDate = dateFns.subWeeks(now, 1);
				break;
			case PeriodType.CURRENT_PITCHING_WEEK:
				now = getCurrentPitchingWeek().now;
				fromDate = getCurrentPitchingWeek().fromDate;
				break;
			case PeriodType.NEXT_PITCHING_WEEK:
				now = getNextPitchingWeek().now;
				fromDate = getNextPitchingWeek().fromDate;
				break;
			case PeriodType.LAST_4_WEEKS:
				fromDate = dateFns.subWeeks(now, 4);
				break;
			case PeriodType.LAST_8_WEEKS:
				fromDate = dateFns.subWeeks(now, 8);
				break;
			case PeriodType.LAST_12_WEEKS:
				fromDate = dateFns.subWeeks(now, 12);
				break;
			case PeriodType.SINCE_START_YEAR:
				fromDate = dateFns.startOfYear(now);
				break;
		}

		let fromDateFormat = 'dd';
		let nowDateFormat = 'dd MMM';
		if (!dateFns.isSameYear(now, fromDate)) {
			fromDateFormat = 'P';
			nowDateFormat = 'P';
		} else if (!dateFns.isSameMonth(now, fromDate)) {
			fromDateFormat = 'dd MMM';
		}

		return `${formatDate(
			isUTC ? toZonedTime(fromDate, 'UTC') : fromDate,
			fromDateFormat,
		)} - ${dateFns.format(
			isUTC ? toZonedTime(now, 'UTC') : now,
			nowDateFormat,
		)}`;
	}, [formatDate, isUTC, period]);

	return (
		<HStack
			cursor="pointer"
			_hover={{bg: 'background.neutral.tertiary.default'}}
			borderRadius={4}
			py={2}
			pe={2}
			onClick={handleClick}
		>
			<CheckIcon
				color="icon.accent.primary.default"
				boxSize={4}
				margin={1}
				mr={3}
				opacity={isActive ? 1 : 0}
			/>
			<Flex flex={1} justifyContent="space-between">
				<Text
					data-testid={datePickerDataTestId(period)}
					variant="body.small.regular"
					color={
						isActive
							? 'text.accent.primary.default'
							: 'text.neutral.primary.default'
					}
				>
					{getPeriodText(t, period)}
				</Text>
				<Text
					variant="body.small.regular"
					color="text.neutral.secondary.default"
				>
					{periodTimeLabel}
				</Text>
			</Flex>
		</HStack>
	);
};

const PeriodSelector = ({
	value,
	options,
	onChange,
	isUTC,
}: DateRangePickerProps<RangeSelectorType.PERIOD>) => {
	const periods = useMemo(() => {
		return options.periods || Object.values(PeriodType);
	}, [options.periods]);
	return (
		<Box>
			{periods.map(p => (
				<Period
					key={p}
					period={p}
					isActive={p === value?.period}
					onClick={onChange}
					isUTC={isUTC}
				/>
			))}
		</Box>
	);
};

export default PeriodSelector;
